import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { diffInDays } from 'lib/datetime/dateUtils'

/**
 * Number of secondary YMAL tiles to render, hydrate, and warm immediately.
 * The primary searched property is counted separately.
 */
export function getMetaSearchPrioritySecondaryTileCount(isMobileDevice: boolean): number {
  return isMobileDevice ? 1 : 3
}

/**
 * Builds the `App.MetaSearchObject` describing the YMAL list anchor for a
 * meta-search property page. Returns `undefined` when the offer is missing or
 * not a bedbank offer (the only type that supports YMAL today).
 *
 * Shared between SSR (`fetchMetaSearchOffer.ts`) and client
 * (`MetaSearchPage.tsx`) so both sides compute identical filter shapes — the
 * downstream `getOfferListKey` is sensitive to field order/types and any drift
 * causes the SSR'd state to be ignored at hydration (silent re-fetch).
 */
export function buildMetaSearchObjectFromOffer(
  offer: App.AnyOffer | undefined,
  region: string,
  filters: { checkIn?: string, checkOut?: string },
  options: {
    limit?: number,
    forcedRankingsDistance?: App.ForcedRankingsDistance,
    /** Offer countries the treatment applies to; empty or absent means every country. */
    forcedRankingsOfferCountryCodes?: Array<string>,
  } = {},
): App.MetaSearchObject | undefined {
  if (!offer || !isBedbankOffer(offer)) return undefined
  const latitude = offer.property?.latitude
  const longitude = offer.property?.longitude
  if (latitude == null || longitude == null) return undefined

  const offerCountry = offer.property?.address.countryCode ?? region
  // The router matches on the website region only; the treatment's country list is what scopes
  // it to the hotel's country. A page outside that list behaves as today (no override, same key).
  const forcedRankingsCountries = options.forcedRankingsOfferCountryCodes ?? []
  const forcedRankingsDistance = forcedRankingsCountries.length === 0 || forcedRankingsCountries.includes(offerCountry) ?
    options.forcedRankingsDistance :
    undefined

  return {
    region,
    offerCountry,
    latitude,
    longitude,
    disabled: false,
    limit: options.limit ?? 20,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    offerId: offer.id,
    // Only present for visits in a forced-rankings experiment, on the countries it targets;
    // keeps the key (and SSR cache) distinct per variant.
    ...(forcedRankingsDistance ? { forcedRankingsDistance } : {}),
  }
}

/**
 * Builds the `App.OfferListFilters` consumed by `fetchMetaOfferIds` /
 * `getOfferListKey`. Field set MUST stay in sync with the client-side
 * `useMetaSearchOfferList` builder so the SSR-populated `state.offer.offerLists`
 * slice is recognised on hydration.
 */
export function buildMetaSearchOfferListFilters(
  metaSearchObject: App.MetaSearchObject,
  searchDisabled = false,
): App.OfferListFilters {
  return {
    metaSearchObject: {
      region: metaSearchObject.region,
      offerCountry: metaSearchObject.offerCountry,
      latitude: metaSearchObject.latitude,
      longitude: metaSearchObject.longitude,
      limit: metaSearchObject.limit ?? 20,
      disabled: searchDisabled,
      offerId: metaSearchObject.offerId,
      ...(metaSearchObject.forcedRankingsDistance ? { forcedRankingsDistance: metaSearchObject.forcedRankingsDistance } : {}),
    },
  }
}

/**
 * Parent-level YMAL tile filters. YMAL secondary offers always render dateless
 * ("anytime") pricing — the user's incoming dates are not forwarded here.
 */
export function buildYmalTileFilters(propertyId: string): App.OfferListFilters {
  return { showDistanceWithMetaOfferId: propertyId }
}

/**
 * GR-4746 follow-up — strip URL dates from a tile's click-through when no
 * package matches the requested duration. Prevents the offer page hanging when
 * min-stay > requested duration (e.g. 1-night URL into a 2-night Flash). Only
 * applies to LE hotels (Flash / LPC / LM); bedbank (LPP) accepts any duration
 * via per-night rates, so dates always flow through unchanged.
 */
export function buildOfferTileUrlFilters(
  filters: App.OfferListFilters | undefined,
  offer: App.AnyOffer,
): App.OfferListFilters | undefined {
  if (!filters?.checkIn || !filters?.checkOut) return filters
  // Only LE hotels (Flash / LPC / LM) have duration-keyed packages we can match
  // against. Bedbank, tours, cruises, villas, bundles either accept any
  // duration or don't expose `packages` in the LE-hotel shape — leave dates
  // intact so the destination page can handle them normally.
  if (!isLEHotel(offer)) return filters
  const requestedDuration = diffInDays(new Date(filters.checkIn), new Date(filters.checkOut))
  if (requestedDuration <= 0) return filters
  const anyPackageMatches = offer.packages?.some?.((pkg) => pkg.duration === requestedDuration)
  if (anyPackageMatches) return filters
  const { checkIn: _ci, checkOut: _co, ...rest } = filters
  return rest
}
